import type {
  BranchChangeset,
  BranchChangesets,
  ParameterDimensionDefinitionInOut,
  ParameterValueAmendOperation,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import { listUtils, notEmpty, type Optional } from '@festina-life/core'

import type { StackableType } from '../../../components/stackableTable/types'
import type { ParameterDetailsField } from '../../../types/parameterModel'
import type { OperationWithSourceInfo } from '../../../uiModels/types'
import {
  AddParameterDefinitionDimensionPlaceholder,
  isAddParameterDefinitionDimensionPlaceholder,
  type ParameterDimensionDefinitionRowData,
} from './types'

export const toStackableType = (
  dimensionData: ParameterDimensionDefinitionRowData,
  viewingDetails: Optional<ParameterDetailsField>,
): StackableType<ParameterDimensionDefinitionRowData> => ({
  value: dimensionData,
  columns: dimensionData === AddParameterDefinitionDimensionPlaceholder ? 1 : 3,
  key: (index) =>
    dimensionData === AddParameterDefinitionDimensionPlaceholder
      ? dimensionData.toString()
      : `${(dimensionData as ParameterDimensionDefinitionInOut).name ?? ''}/${index}`,
  ratios:
    dimensionData === AddParameterDefinitionDimensionPlaceholder ? [] : [1, 1],
  errors: [],
  isEmpty: dimensionData === AddParameterDefinitionDimensionPlaceholder,
  isActive:
    !isAddParameterDefinitionDimensionPlaceholder(dimensionData) &&
    dimensionData === viewingDetails,
})

/**
 * Packages the given amendments as a downloadable document, one changeset per block they
 * come from. The caller decides which amendments it covers, so the same helper produces the
 * whole parameter's amendments and the single one a reviewer has selected.
 */
export const toAmendmentsBranchChangesets = ({
  amendments,
  layer,
}: {
  amendments: OperationWithSourceInfo<ParameterValueAmendOperation>[]
  layer: LayerKey
}): BranchChangesets => {
  const groupedByAmendments = listUtils.groupBy(
    amendments,
    (amendment) => amendment.block,
  )

  return {
    _type: 'BranchChangesets',
    branchChangesets: [...groupedByAmendments].map(
      ([block, amendmentsInBlock]) =>
        toBranchChangeset({ block, layer, amendments: amendmentsInBlock }),
    ),
  }
}

const toBranchChangeset = ({
  block,
  layer,
  amendments,
}: {
  block: string
  layer: LayerKey
  amendments: OperationWithSourceInfo<ParameterValueAmendOperation>[]
}): BranchChangeset => {
  const operations = amendments.map(
    ({ internalOperation }) => internalOperation,
  )
  const includedOperationKeys = new Set(
    operations.map(({ operationKey }) => operationKey).filter(notEmpty),
  )
  const changesetKey = crypto.randomUUID()

  return {
    _type: 'BranchChangeset',
    block,
    changeset: {
      _type: 'ConfigurationChangeset',
      changesetKey,
      operations,
      layer: layer.layerId,
      tenantId: layer.tenantId,
    },
    changesetHistory: {
      _type: 'ChangesetHistory',
      originSets: [
        {
          _type: 'OperationOriginSet',
          exportChangesetKey: changesetKey,
          operationKeys: Array.from(includedOperationKeys),
        },
      ],
    },
  }
}
